import { useFormContext, useWatch } from 'react-hook-form';
import type { Condition, ValueCondition } from '../../types/widget.js';
import { getHuckConfig } from '../utils/getHuckConfig.js';

const checkCondition = (condition: Condition, value?: string | number | boolean) => {
  const conditionValue =
    'current-year' === (condition as ValueCondition).value
      ? new Date().getFullYear()
      : ((condition as ValueCondition).value ?? '');

  if (condition.operator === 'falsyOrAbsent') {
    const falsyValues = ['', 0, -1, false, null, undefined, 'NO'];
    return falsyValues.includes(value);
  }

  if (value === undefined) {
    // If the value is undefined, the condition can only be true if it checks for absence - otherwise we return false to avoid further checks
    return condition.operator === 'absent';
  }

  switch (condition.operator) {
    case 'eq':
      return value === conditionValue || value.toString() === conditionValue.toString();
    case 'ne':
      return value !== conditionValue && value.toString() !== conditionValue.toString();
    case 'gt':
      return value > conditionValue;
    case 'lt':
      return value < conditionValue;
    case 'gte':
      return value >= conditionValue;
    case 'lte':
      return value <= conditionValue;
    case 'in': {
      const arr = Array.isArray(conditionValue) ? conditionValue : [conditionValue];
      return arr.includes(value) || arr.includes(value.toString());
    }
    case 'exists':
      return true; // We've checked against undefined at the start
    default:
      return false;
  }
};

export const useConditions = (conditions: Condition[] | undefined) => {
  const config = getHuckConfig();
  const { control } = useFormContext();
  const fieldNames = conditions?.filter((condition) => 'field' in condition).map((condition) => condition.field) ?? [];
  const watchValue = useWatch({
    control,
    name: fieldNames,
    disabled: fieldNames.length === 0,
  });

  let fieldIndex = 0;

  return conditions
    ? conditions.reduce((result, condition) => {
        if ('field' in condition) {
          return result && checkCondition(condition, watchValue[fieldIndex++]);
        }

        if ('context' in condition) {
          let value: any;

          if (condition.context === 'brand') {
            value = config.brand;
          } else if (condition.context === 'mode') {
            value = config.mode;
          } else if (condition.context === 'locale') {
            value = config.locale;
          } else if (condition.context === 'orgId') {
            value = config.orgId;
          } else {
            return result;
          }

          return result && checkCondition(condition, value);
        }

        return result;
      }, true)
    : true;
};
